import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { fetchUser } from '@/api';

/**
 * Props for the AuthProvider component.
 */
interface AuthProviderProps {
  /** The child components that will have access to the authentication context. */
  children?: ReactNode;
}

/**
 * The shape of the authentication context state.
 */
interface AuthContextType {
  /** The currently authenticated user object, or null if not logged in. */
  user: any; 
  /** Indicates whether the authentication status is still being determined. */
  loading: boolean;
}

/**
 * Valid claims that may be present in the user object.
 * (Documentation of expected OIDC claims)
    sub
    name
    email
    ver
    iss
    aud
    iat
    exp
    jti
    amr
    idp
    nonce
    preferred_username
    auth_time
    at_hash
    lastName
    nyssoguid
    mail
    OU
    testUserID
    middleinitial
    EmployeeType
    nylastdamodifytime
    firstName
    email_claim
    organization
    USERID
    e-mail
    middleName
    uid_claim
    testnyssoguid
    nyappitsaiassist
 */

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Provides authentication state to the application.
 * Automatically fetches the current user on mount.
 * 
 * @param {AuthProviderProps} props - The component props.
 * @returns {JSX.Element} The context provider component.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        /**
         * Fetches user data from the API and updates state.
         */
        const getUser = async () => {
            try {
                const userData = await fetchUser();
                if (userData) {
                    setUser(userData);
                } else {
                    setUser(null);
                }
            } catch (error) {
                console.error('Failed to fetch user:', error);
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        getUser();
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * Custom hook to access the authentication context.
 * Must be used within an AuthProvider.
 * 
 * @throws {Error} If used outside of an AuthProvider.
 * @returns {AuthContextType} The authentication context value.
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
